import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiChevronDown } from "react-icons/fi";
import {
  FaBalanceScale,
  FaCalculator,
  FaChartLine,
  FaFileAlt,
} from "react-icons/fa";
import { useLanguage } from "../components/idiomas";
import Footer from "../components/footer";

export default function Services() {
  const { language, translations } = useLanguage();
  const t = translations[language].practiceAreas;

  const [activeCard, setActiveCard] = useState(null);
  const [activeSubarea, setActiveSubarea] = useState(null);

  // 🔹 Mapa de íconos por área
  const iconMap = {
    legal: <FaBalanceScale className="text-5xl text-white" />,
    accounting: <FaCalculator className="text-5xl text-white" />,
    development: <FaChartLine className="text-5xl text-white" />,
    notarial: <FaFileAlt className="text-5xl text-white" />,
  };

  const toggleCard = (index) => {
    setActiveCard(activeCard === index ? null : index);
    setActiveSubarea(null);
  };

  const toggleSubarea = (id) => {
    setActiveSubarea(activeSubarea === id ? null : id);
  };

  return (
    <div className="min-h-screen flex flex-col font-sans bg-white text-[#1E2E45]">
      {/* 🔷 Sección principal */}
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative py-28 px-6 md:px-16 bg-fixed bg-cover bg-center text-white overflow-hidden"
        style={{ backgroundImage: "url('/images/fondoservicios.png')" }}
      >
        <div className="absolute inset-0 bg-[#0b1c2c]/80 backdrop-blur-[2px] z-0" />

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold mb-6 text-white drop-shadow-lg"
          >
            {t.mainTitle}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-lg md:text-xl font-light text-gray-200 max-w-3xl mx-auto mb-20"
          >
            {t.subtitle}
          </motion.p>

          {/* 🔹 Tarjetas de áreas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.areas.map((area, index) => (
              <motion.div
                key={area.key || index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
                onClick={() => toggleCard(index)}
                className="relative bg-white/10 border border-white/20 rounded-3xl p-8 backdrop-blur-lg shadow-xl hover:shadow-2xl hover:bg-white/20 hover:-translate-y-2 transition-all duration-500 cursor-pointer"
              >
                <div className="flex justify-center mb-4">
                  {/* 🔹 Renderiza el ícono según el área */}
                  {iconMap[area.key]}
                </div>

                <h3 className="text-2xl font-semibold mb-2 text-white">
                  {area.title}
                </h3>

                <p className="text-sm text-gray-200 mb-3">
                  {area.shortDescription}
                </p>

                <div className="w-16 h-[3px] text-white mx-auto mb-5 rounded-full" />

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCard(index);
                  }}
                  className="flex justify-center items-center gap-2 text-white hover:text-white transition-colors text-sm"
                >
                  {activeCard === index
                    ? t.buttons.showLess
                    : t.buttons.showMore}
                  <motion.span
                    animate={{ rotate: activeCard === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FiChevronDown className="text-lg" />
                  </motion.span>
                </button>

                {/* 🔹 Contenido desplegable */}
                <AnimatePresence>
                  {activeCard === index && (
                    <motion.div
                      key="desc"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4 }}
                      className="mt-4 text-gray-100 text-sm text-left"
                    >
                      <p className="mb-3">{area.detailedDescription}</p>
                      <ul className="space-y-2">
                        {area.items.map((item, i) => (
                          <li key={i} className="ml-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSubarea(`${index}-${i}`);
                              }}
                              className="flex justify-between items-center w-full text-left hover:text-white transition-colors"
                            >
                              <span>• {item.name}</span>
                              <motion.span
                                animate={{
                                  rotate:
                                    activeSubarea === `${index}-${i}` ? 180 : 0,
                                }}
                                transition={{ duration: 0.3 }}
                              >
                                <FiChevronDown className="text-sm" />
                              </motion.span>
                            </button>

                            <AnimatePresence>
                              {activeSubarea === `${index}-${i}` && (
                                <motion.p
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="ml-6 mt-1 text-gray-300 text-xs"
                                >
                                  {item.description}
                                </motion.p>
                              )}
                            </AnimatePresence>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.main>

      {/* 🔷 Filosofía */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="bg-white py-24 text-[#1E2E45]"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-left mb-12">
            <h2 className="text-3xl md:text-4xl font-bold leading-snug">
              {t.philosophy.title} <br></br>
              {t.philosophy.subtitleTi}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 text-justify text-lg leading-relaxed font-light">
            {t.philosophy.paragraphs.map((p, i) => (
              <p key={i} className="mb-3 last:mb-0">
                {p}
              </p>
            ))}
          </div>
        </div>
      </motion.section>

      {/* 🔷 Por qué elegirnos */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="bg-[#F7F9FB] py-14"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1E2E45] mb-12">
            {t.whyChooseUs.title}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {t.whyChooseUs.cards.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="flex flex-col items-center text-center bg-white rounded-xl p-6 transition-transform duration-300 hover:scale-105 hover:shadow-2xl"
              >
                <img
                  src={item.img}
                  alt={item.title}
                  className="rounded-lg shadow-md mb-6 w-full h-56 object-cover transition duration-300"
                />
                <h3 className="text-xl font-semibold text-[#1E2E45] mb-2">
                  {item.title}
                </h3>
                <p className="text-[#1E2E45] font-light text-sm md:text-base">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <Footer />
    </div>
  );
}